import {
  HttpClient,
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { BrowserModule } from '@angular/platform-browser';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import {
  provideTranslateService,
  TranslateLoader,
  TranslateModule,
} from '@ngx-translate/core';
import {
  provideTranslateHttpLoader,
  TranslateHttpLoader,
} from '@ngx-translate/http-loader';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppConfig } from './app.config';
import { BreadcrumbsComponent } from './breadcrumbs/breadcrumbs.component';
import { FooterComponent } from './footer/footer/footer.component';
import { HeaderComponent } from './header/header/header.component';
import { DepartmentsComponent } from './journey/departments/departments.component';
import { LocationComponent } from './journey/location/location.component';
import { LoginComponent } from './journey/login/login.component';
import { ServicesComponent } from './journey/services/services.component';
import { AlertModule } from './_alert';
import { CalendarComponent } from './journey/calendar/calendar.component';
import { BookingDetailsComponent } from './journey/booking-details/booking-details.component';
import { SummaryComponent } from './journey/summary/summary.component';
import { ConfirmationComponent } from './journey/confirmation/confirmation.component';
import { SessionComponent } from './journey/session/session.component';
import { LeafletMapComponent } from './leaflet-map/leaflet-map.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, DateAdapter } from '@angular/material/core';
import { CustomDateAdapter } from './journey/calendar/custom-date-adapter';
import { BookingSummaryComponent } from './booking-summary/booking-summary.component';
import { MainAccountComponent } from './account-journey/main-account/main-account.component';
import { SkipToContentComponent } from './skip-to-content/skip-to-content.component';

// Material
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSliderModule } from '@angular/material/slider';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatListModule } from '@angular/material/list';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatCardModule } from '@angular/material/card';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTabsModule } from '@angular/material/tabs';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatPaginatorModule } from '@angular/material/paginator';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RegisterComponent } from './journey/register/register.component';
import { SanitiseHtmlPipe } from './pipes/sanitise-html.pipe';

import { NgxSpinnerModule } from 'ngx-spinner';
import { MemberBarComponent } from './member-bar/member-bar.component';
import { MyAccountComponent } from './account-journey/my-account/my-account.component';
import { ChangePasswordComponent } from './account-journey/change-password/change-password.component';
import { UpcomingBookingsComponent } from './account-journey/upcoming-bookings/upcoming-bookings.component';
import { PastBookingsComponent } from './account-journey/past-bookings/past-bookings.component';
import { AccountLoginComponent } from './account-journey/account-login/account-login.component';
import { ModalComponent } from './modal/modal.component';
import { MatCalendarComponent } from './journey/mat-calendar/mat-calendar.component';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { SvgComponent } from './header/header/svg/svg.component';
import { HttpInterceptorInterceptor } from './services/http-interceptor.interceptor';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NotificationsComponent } from './header/notifications/notifications.component';
import { DeepLinksComponent } from './journey/deeplink/deeplink.component';
import { PermitsComponent } from './journey/permits/permits.component';
import { PermitsSubmittedComponent } from './journey/permits-submitted/permits-submitted.component';
import { AmendPermitsComponent } from './journey/amend-permits/amend-permits.component';
import { AmendPermitsSubmittedComponent } from './journey/amend-permits-submitted/amend-permits-submitted.component';
import { AddressLookupStatusComponent } from '@bookinglab/hwrc-ui';

export function initializeApp(appConfig: AppConfig) {
  return () => appConfig.load();
}

@NgModule({
  declarations: [
    AppComponent,
    LocationComponent,
    DepartmentsComponent,
    HeaderComponent,
    FooterComponent,
    LoginComponent,
    BreadcrumbsComponent,
    SkipToContentComponent,
    ServicesComponent,
    CalendarComponent,
    BookingDetailsComponent,
    SummaryComponent,
    ConfirmationComponent,
    SessionComponent,
    LeafletMapComponent,
    BookingSummaryComponent,
    RegisterComponent,
    MemberBarComponent,
    SanitiseHtmlPipe,
    MainAccountComponent,
    MyAccountComponent,
    ChangePasswordComponent,
    UpcomingBookingsComponent,
    PastBookingsComponent,
    AccountLoginComponent,
    ModalComponent,
    MatCalendarComponent,
    SvgComponent,
    NotificationsComponent,
    DeepLinksComponent,
    PermitsComponent,
    PermitsSubmittedComponent,
    AmendPermitsComponent,
    AmendPermitsSubmittedComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    TranslateModule,
    AlertModule,
    NgbModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatCheckboxModule,
    MatButtonModule,
    MatInputModule,
    MatAutocompleteModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatRadioModule,
    MatSelectModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatMenuModule,
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatGridListModule,
    MatCardModule,
    MatStepperModule,
    MatTabsModule,
    MatExpansionModule,
    MatButtonToggleModule,
    MatChipsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatDialogModule,
    MatTooltipModule,
    MatSnackBarModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    BrowserAnimationsModule,
    FormsModule,
    NgxSpinnerModule,
    FontAwesomeModule,
  ],
  providers: [
    AppConfig,
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/assets/i18n/',
        suffix: '.json',
      }),
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [AppConfig],
      multi: true,
    },
    {
      provide: DateAdapter,
      useClass: CustomDateAdapter,
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'en-GB',
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpInterceptorInterceptor,
      multi: true,
    },
    provideHttpClient(withInterceptorsFromDi()),
    AddressLookupStatusComponent
  ],
})
export class AppModule {}
